import type {
	ChartConfig,
	ChartTooltipProps,
} from '@/shared/components/ui/chart'

import { flatten, isNil, isNumber, uniq } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceArea,
	ReferenceLine,
	XAxis,
	YAxis,
} from 'recharts'

import { ChartContainer, ChartTooltip } from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

/**
 * Recharts (shadcn charts) rendering of the Limitations price-interval chart.
 *
 * Faithful port of {@link ChartView} (Highcharts `dumbbell` + `inverted`) for
 * the charting-library migration spike (PCT-976): one horizontal dumbbell per
 * limit series (lower/upper range as a connector with end markers) over a
 * numeric price value axis, plus a shaded band marking the available / conflict
 * price interval and a vertical line marking the current price.
 *
 * Recharts has no native dumbbell type, so each series is a single floating
 * range `Bar` (`[low, high]`) on a hidden category y-axis (`layout="vertical"`
 * = Highcharts `inverted`) drawn with a custom shape: a connector line plus
 * circle/square end markers, dashed for `highlight` series. An infinite (null)
 * bound is clamped to the axis edge and renders without a marker, so the
 * connector reads as running off to Infinity. The Highcharts plotBand maps to a
 * `ReferenceArea` and the current-price plotLine to a `ReferenceLine`. The prop
 * shape mirrors the Highcharts version so the consumer can feed identical data
 * to either implementation.
 */

const LAST_PRICE_COLOR = '#E87B35'

type ChartProps = {
	series: {
		name: string
		range: (number | null)[]
		color: string
		type: 'correct' | 'highlight'
	}[]
	last_price?: number | null
	plotBand: { from: number | null; to: number | null; color: string }
	getLabel: (value: string) => string
}

type ChartRow = {
	key: string
	label: string
	color: string
	type: 'correct' | 'highlight'
	// Original (possibly null) bounds, used for the tooltip text.
	points: (number | null)[]
	// Axis-clamped bounds fed to the range bar.
	range: [number, number]
	hasLow: boolean
	hasHigh: boolean
}

// Mirrors `normalizePlotBandTicksPosition`: clamp open-ended bounds to the axis
// extent and widen a zero-width band so it stays visible.
const getBandBounds = (
	plotBand: ChartProps['plotBand'],
	min: number,
	max: number
): { from: number; to: number } | null => {
	const { from, to } = plotBand

	if (isNil(from) && isNil(to)) {
		return null
	}

	const minWidth = 0.2
	const adjustedTo = from === to ? to! + minWidth : to

	return {
		from: isNil(from) ? min : from,
		to: isNil(adjustedTo) ? max : adjustedTo,
	}
}

// Custom dumbbell shape: a connector line spanning [low, high] with end markers
// (square for highlight, circle otherwise). The marker is omitted on an
// infinite side so the connector reads as continuing past the axis edge.
const DumbbellShape = (props: {
	x?: number
	y?: number
	width?: number
	height?: number
	payload?: ChartRow
}) => {
	const { x = 0, y = 0, width = 0, height = 0, payload } = props

	if (!payload) {
		return null
	}

	const cy = y + height / 2
	const x1 = x
	const x2 = x + width
	const { color, type, hasLow, hasHigh } = payload
	const isHighlight = type === 'highlight'
	const r = 4

	const marker = (cx: number, suffix: string) =>
		isHighlight ? (
			<rect
				key={suffix}
				x={cx - r}
				y={cy - r}
				width={r * 2}
				height={r * 2}
				fill={color}
			/>
		) : (
			<circle key={suffix} cx={cx} cy={cy} r={r} fill={color} />
		)

	return (
		<g>
			<line
				x1={x1}
				y1={cy}
				x2={x2}
				y2={cy}
				stroke={color}
				strokeWidth={2}
				strokeDasharray={isHighlight ? '6 4' : undefined}
			/>
			{hasLow && marker(x1, 'low')}
			{hasHigh && marker(x2, 'high')}
		</g>
	)
}

// Reproduces `tooltipFormatter`: series name header + "low - high" body using
// the shared `.chart-tooltip` style so it is visually identical to Highcharts.
const DumbbellTooltip = ({ active, payload }: ChartTooltipProps) => {
	if (!active || !payload?.length) {
		return null
	}

	const row = payload[0].payload as ChartRow
	const points = row.points

	return (
		<div className='chart-tooltip space-y-1 text-xs'>
			<div>{row.label}</div>
			<div>
				{' '}
				{isNil(points[0])
					? 'Infinity'
					: formatNumber(points[0], NumberFormats.Fixed)}{' '}
				-{' '}
				{isNil(points[1])
					? 'Infinity'
					: formatNumber(points[1] as number, NumberFormats.Fixed)}{' '}
			</div>
		</div>
	)
}

const toRows = (
	series: ChartProps['series'],
	getLabel: ChartProps['getLabel']
) =>
	series.map((serie, index): ChartRow => {
		const hasLow = !isNil(serie.range[0])
		const hasHigh = !isNil(serie.range[1])

		return {
			key: String(index),
			label: getLabel(serie.name),
			color: serie.color,
			type: serie.type,
			points: serie.range,
			// Filled below once axis bounds are known.
			range: [0, 0],
			hasLow,
			hasHigh,
		}
	})

const toConfig = (series: ChartProps['series']): ChartConfig =>
	Object.fromEntries(
		series.map((serie, index) => [
			String(index),
			{ label: serie.name, color: serie.color },
		])
	)

export default function ChartView4({
	series,
	last_price,
	plotBand,
	getLabel,
}: ChartProps) {
	const { t } = useTranslation()
	const points = uniq(
		flatten([
			...[isNil(plotBand.from) ? 0 : plotBand.from, plotBand.to, last_price],
			...series.map(({ range }) => range),
		])
	).filter((i): i is number => i != null && isNumber(i))
	const min = Math.min(...points)
	const max = Math.max(...points)

	// Pad the value axis so finite endpoints never touch the chart edge — they
	// keep visible space (and a marker) on both sides. Null (infinite) bounds, by
	// contrast, reach all the way out to the padded edge and render without a
	// marker, so they read as running off to Infinity instead of sitting inside
	// the padding like a finite endpoint does.
	const span = max - min || 1
	const pad = span * 0.1
	const axisMin = min - pad
	const axisMax = max + pad

	const rows = toRows(series, getLabel).map((row) => ({
		...row,
		range: [
			row.hasLow ? row.points[0]! : axisMin,
			row.hasHigh ? row.points[1]! : axisMax,
		] as [number, number],
	}))

	const band = getBandBounds(plotBand, axisMin, axisMax)

	return (
		<ChartContainer config={toConfig(series)} className='h-57 w-full fade-in'>
			<BarChart
				accessibilityLayer={false}
				layout='vertical'
				data={rows}
				margin={{ top: 16, right: 8, bottom: 8, left: 8 }}
			>
				<CartesianGrid horizontal={false} />
				<XAxis
					type='number'
					domain={[axisMin, axisMax]}
					tickCount={6}
					fontSize={10}
					tickLine={false}
					axisLine={false}
					tickFormatter={(value) =>
						formatNumber(Number(value), NumberFormats.Fixed)
					}
				/>
				<YAxis type='category' dataKey='key' hide />
				{band && (
					<ReferenceArea
						x1={band.from}
						x2={band.to}
						fill={plotBand.color}
						fillOpacity={1}
						stroke='none'
					/>
				)}
				<ChartTooltip cursor={false} content={<DumbbellTooltip />} />
				<Bar
					dataKey='range'
					isAnimationActive={false}
					shape={<DumbbellShape />}
				/>
				{!isNil(last_price) && (
					<ReferenceLine
						x={last_price}
						stroke={LAST_PRICE_COLOR}
						strokeWidth={4}
						label={{
							value: t('widget.current_price', {
								defaultValue: 'Current price',
							}),
							position: 'top',
							fill: 'var(--foreground)',
							fontSize: 10,
						}}
					/>
				)}
			</BarChart>
		</ChartContainer>
	)
}
